import React, { useState, useEffect } from 'react';
import {
  CheckCircle2,
  XCircle,
  Clock,
  ClipboardList,
  ChevronDown,
  ChevronUp,
  Plus,
  Trash2,
  Save,
  Loader2,
  AlertCircle,
  MessageSquare,
  Layers,
  Star,
  StarOff,
  Eye
} from 'lucide-react';
import { contractsApi } from '../../lib/api';
import useWorkflowStore from '../../stores/workflowStore';

// ─────────────────────────────────────────────
// Inferential phase config
// ─────────────────────────────────────────────
const PHASE_CONFIG = {
  attentional_orienting:           { label: 'Attentional Orienting',          text: 'text-pink-700',   border: 'border-l-pink-400'   },
  precision_establishment:         { label: 'Precision Establishment',        text: 'text-purple-700', border: 'border-l-purple-400' },
  dominant_model_alignment:        { label: 'Dominant Model Alignment',       text: 'text-indigo-700', border: 'border-l-indigo-400' },
  observational_counter_evidence:  { label: 'Observational Counter-Evidence', text: 'text-blue-700',   border: 'border-l-blue-400'   },
  epistemic_instability:           { label: 'Epistemic Instability',          text: 'text-cyan-700',   border: 'border-l-cyan-400'   },
  spontaneous_inference_of_s_star: { label: 'Spontaneous Inference of S*',    text: 'text-teal-700',   border: 'border-l-teal-400'   },
  policy_naturalization:           { label: 'Policy Naturalization',          text: 'text-green-700',  border: 'border-l-green-400'  },
};

const S_STAR_FIELDS = [
  { key: 'category_identifiable',    label: 'Categoria identificabile',       hint: 'La categoria del prodotto può essere correttamente identificata?' },
  { key: 'mechanism_identifiable',   label: 'Meccanismo ricostruibile',       hint: 'Il meccanismo di funzionamento può essere ricostruito dalle osservazioni?' },
  { key: 'benefit_identifiable',     label: 'Beneficio inferibile',           hint: 'Il beneficio funzionale è inferibile senza spiegazioni esterne?' },
  { key: 'policy_identifiable',      label: 'Policy comprensibile',           hint: 'La policy di verifica è comprensibile?' },
  { key: 'overall_identifiability',  label: 'S* ricostruibile complessivamente', hint: 'Lo stato alternativo latente è ricostruibile autonomamente da un primo visualizzatore?' },
];

const EMPTY_S_STAR = {
  category_identifiable: '',
  mechanism_identifiable: '',
  benefit_identifiable: '',
  policy_identifiable: '',
  overall_identifiability: ''
};

// ─────────────────────────────────────────────
// SStarForm — s_star_identifiability form
// ─────────────────────────────────────────────
const SStarForm = ({ data = {}, onChange, readOnly = false }) => (
  <div className="space-y-2">
    {S_STAR_FIELDS.map(({ key, label, hint }) => (
      <div key={key}>
        <label className="block text-xs font-medium text-gray-600 mb-1" title={hint}>{label}</label>
        {readOnly ? (
          <p className="text-xs text-gray-800 bg-gray-50 rounded px-2 py-1.5">{data[key] || '—'}</p>
        ) : (
          <select
            value={data[key] || ''}
            onChange={e => onChange({ ...data, [key]: e.target.value })}
            className="w-full text-xs border border-gray-300 rounded px-2 py-1.5 focus:ring-2 focus:ring-fuchsia-400 focus:border-transparent bg-white"
          >
            <option value="">— non valutato —</option>
            <option value="sì">Sì</option>
            <option value="parzialmente">Parzialmente</option>
            <option value="no">No</option>
          </select>
        )}
      </div>
    ))}
  </div>
);

// ─────────────────────────────────────────────
// ScriptReviewCard — singolo script selezionabile
// ─────────────────────────────────────────────
const ScriptReviewCard = ({
  script, index, reviewState, onToggle,
  sStarData, onSStarChange,
  rejectionReason, onRejectionReasonChange,
  isSelected, onSelect, showSelectButton,
  selectionRationale, onSelectionRationaleChange,
  isReadOnly
}) => {
  const [expanded, setExpanded] = useState(false);
  const [showSegments, setShowSegments] = useState(false);
  const [showSStar, setShowSStar] = useState(false);

  const statusColors = {
    approved: isSelected ? 'border-fuchsia-500 bg-fuchsia-50' : 'border-green-400 bg-green-50',
    rejected: 'border-red-400 bg-red-50',
    pending:  'border-gray-200 bg-white'
  };

  const badgeColors = {
    approved: isSelected ? 'bg-fuchsia-100 text-fuchsia-700' : 'bg-green-100 text-green-700',
    rejected: 'bg-red-100 text-red-700',
    pending:  'bg-gray-100 text-gray-500'
  };

  const profile = script.implementation_profile || {};
  const segments = script.script_segments || [];

  return (
    <div className={`rounded-lg border-2 overflow-hidden transition-colors ${statusColors[reviewState]}`}>
      {/* Header */}
      <div className="p-3 flex items-center gap-3">
        {/* Approve / Reject buttons */}
        {!isReadOnly && (
          <>
            <button
              onClick={() => onToggle(script.script_id, 'approved')}
              className={`flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full transition-colors
                ${reviewState === 'approved' ? 'bg-green-500 text-white' : 'border-2 border-gray-300 text-gray-300 hover:border-green-400 hover:text-green-400'}`}>
              <CheckCircle2 className="w-4 h-4" />
            </button>
            <button
              onClick={() => onToggle(script.script_id, 'rejected')}
              className={`flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full transition-colors
                ${reviewState === 'rejected' ? 'bg-red-500 text-white' : 'border-2 border-gray-300 text-gray-300 hover:border-red-400 hover:text-red-400'}`}>
              <XCircle className="w-4 h-4" />
            </button>
          </>
        )}

        {/* Title */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-gray-800">
              {script.script_id || `Script ${index + 1}`}
            </span>
            {script.estimated_duration_seconds && (
              <span className="text-xs text-gray-400 bg-white border border-gray-200 px-1.5 py-0.5 rounded">
                {script.estimated_duration_seconds}s
              </span>
            )}
            <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${badgeColors[reviewState]}`}>
              {reviewState === 'approved'
                ? isSelected ? '★ Selezionato' : 'Approvato'
                : reviewState === 'rejected' ? 'Rifiutato' : 'Da rivedere'}
            </span>
          </div>
          {profile.opening_strategy && (
            <p className="text-xs text-gray-400 mt-0.5 truncate">{profile.opening_strategy}</p>
          )}
        </div>

        {/* Select canonical button (only for approved scripts when multiple approved) */}
        {reviewState === 'approved' && showSelectButton && !isReadOnly && (
          <button
            onClick={() => onSelect(script.script_id)}
            title={isSelected ? 'Script canonical selezionato' : 'Seleziona come script canonical'}
            className={`flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full transition-colors
              ${isSelected ? 'bg-fuchsia-500 text-white' : 'border-2 border-gray-300 text-gray-400 hover:border-fuchsia-400 hover:text-fuchsia-500'}`}>
            {isSelected ? <Star className="w-4 h-4" /> : <StarOff className="w-4 h-4" />}
          </button>
        )}

        {/* Expand toggle */}
        <button onClick={() => setExpanded(!expanded)} className="flex-shrink-0 text-gray-400 hover:text-gray-600">
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t border-gray-100">

          {/* Spoken text */}
          {script.spoken_text && (
            <div className="px-4 py-3">
              <div className="flex items-center gap-1.5 mb-2">
                <MessageSquare className="w-3.5 h-3.5 text-pink-500" />
                <p className="text-xs font-semibold uppercase tracking-wide text-pink-600">Testo Completo</p>
              </div>
              <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap bg-pink-50 rounded p-3 border border-pink-100">
                {script.spoken_text}
              </p>
            </div>
          )}

          {/* Script segments — collapsible */}
          {segments.length > 0 && (
            <div className="border-t border-gray-100">
              <button
                onClick={() => setShowSegments(!showSegments)}
                className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors">
                <div className="flex items-center gap-1.5">
                  <Layers className="w-3.5 h-3.5 text-indigo-500" />
                  <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600">Segmenti per Fase</p>
                </div>
                {showSegments ? <ChevronUp className="w-3.5 h-3.5 text-gray-400" /> : <ChevronDown className="w-3.5 h-3.5 text-gray-400" />}
              </button>
              {showSegments && (
                <div className="px-4 pb-3 pt-2 space-y-2">
                  {segments.map((seg, si) => {
                    const cfg = PHASE_CONFIG[seg.inferential_phase] || {};
                    return (
                      <div key={si} className={`pl-3 border-l-4 ${cfg.border || 'border-l-gray-300'}`}>
                        <p className={`text-xs font-semibold uppercase tracking-wide mb-0.5 ${cfg.text || 'text-gray-600'}`}>
                          {cfg.label || seg.inferential_phase}
                        </p>
                        <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">{seg.text}</p>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Rejection reason — only for rejected scripts */}
          {reviewState === 'rejected' && !isReadOnly && (
            <div className="px-4 py-3 border-t border-gray-100">
              <p className="text-xs font-semibold uppercase tracking-wide text-red-600 mb-1.5">Motivo del rifiuto</p>
              <textarea
                value={rejectionReason || ''}
                onChange={e => onRejectionReasonChange(e.target.value)}
                placeholder="Spiega perché questo script viene rifiutato..."
                rows={2}
                className="w-full text-xs border border-red-200 rounded px-2 py-1.5 focus:ring-2 focus:ring-red-400 focus:border-transparent resize-none"
              />
            </div>
          )}
          {reviewState === 'rejected' && isReadOnly && rejectionReason && (
            <div className="px-4 py-3 border-t border-gray-100">
              <p className="text-xs font-semibold uppercase tracking-wide text-red-500 mb-1">Motivo del rifiuto</p>
              <p className="text-xs text-gray-700">{rejectionReason}</p>
            </div>
          )}

          {/* S* Identifiability — only for approved scripts */}
          {reviewState === 'approved' && (
            <div className="border-t border-gray-100">
              <button
                onClick={() => setShowSStar(!showSStar)}
                className="w-full flex items-center justify-between px-4 py-2.5 bg-fuchsia-50 hover:bg-fuchsia-100 transition-colors">
                <div className="flex items-center gap-1.5">
                  <Eye className="w-3.5 h-3.5 text-fuchsia-600" />
                  <p className="text-xs font-semibold uppercase tracking-wide text-fuchsia-700">S* Identifiability</p>
                </div>
                {showSStar ? <ChevronUp className="w-3.5 h-3.5 text-fuchsia-400" /> : <ChevronDown className="w-3.5 h-3.5 text-fuchsia-400" />}
              </button>
              {showSStar && (
                <div className="px-4 pb-4 pt-3 bg-white">
                  <SStarForm
                    data={sStarData || {}}
                    onChange={onSStarChange}
                    readOnly={isReadOnly}
                  />
                </div>
              )}
            </div>
          )}

          {/* Selection rationale — only for the selected script */}
          {reviewState === 'approved' && isSelected && (
            <div className="px-4 py-3 border-t border-fuchsia-100 bg-fuchsia-50">
              <p className="text-xs font-semibold uppercase tracking-wide text-fuchsia-700 mb-1.5">Rationale di selezione</p>
              {isReadOnly ? (
                <p className="text-xs text-gray-700">{selectionRationale || '—'}</p>
              ) : (
                <textarea
                  value={selectionRationale || ''}
                  onChange={e => onSelectionRationaleChange(e.target.value)}
                  placeholder="Spiega perché questo script è stato selezionato come canonical..."
                  rows={2}
                  className="w-full text-xs border border-fuchsia-200 rounded px-2 py-1.5 focus:ring-2 focus:ring-fuchsia-400 focus:border-transparent resize-none bg-white"
                />
              )}
            </div>
          )}

        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────
// StatusBanner
// ─────────────────────────────────────────────
const StatusBanner = ({ execution }) => {
  if (execution.status === 'approved') {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-center gap-2 text-green-700">
          <CheckCircle2 className="w-5 h-5" />
          <span className="font-medium">Revisione approvata</span>
        </div>
      </div>
    );
  }
  if (execution.status === 'rejected') {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center gap-2 text-red-700">
          <XCircle className="w-5 h-5" />
          <span className="font-medium">Revisione rifiutata</span>
        </div>
        {execution.rejectionReason && (
          <p className="text-sm text-red-600 mt-2">{execution.rejectionReason}</p>
        )}
      </div>
    );
  }
  return null;
};

// ─────────────────────────────────────────────
// EmptyState
// ─────────────────────────────────────────────
const EmptyState = () => (
  <div className="flex items-center justify-center h-full">
    <div className="text-center text-gray-500">
      <Clock className="w-12 h-12 mx-auto mb-4 text-gray-300" />
      <p className="text-lg font-medium mb-2">In attesa di esecuzione</p>
      <p className="text-sm">Clicca "Esegui Contract" per aprire la revisione degli script</p>
    </div>
  </div>
);

// ─────────────────────────────────────────────
// Contract12View — REVISIONE UMANA SCRIPT
// ─────────────────────────────────────────────
const Contract12View = ({ execution, onApprove, onReject, loading }) => {
  const { currentProjectId } = useWorkflowStore();

  const output = execution?.output || execution?.outputData || {};
  const scriptLibrary = execution?.inputData?.script_library || output._script_library_snapshot || [];

  const [reviewMap, setReviewMap]                   = useState({});
  const [sStarMap, setSStarMap]                     = useState({});
  const [rejectionReasonMap, setRejectionReasonMap] = useState({});
  const [selectedScriptId, setSelectedScriptId]     = useState(null);
  const [selectionRationale, setSelectionRationale] = useState('');
  const [revisionNotes, setRevisionNotes]           = useState([]);
  const [newNote, setNewNote]                       = useState('');
  const [saving, setSaving]                         = useState(false);
  const [saveError, setSaveError]                   = useState(null);
  const [savedOnce, setSavedOnce]                   = useState(false);
  const [showRejectModal, setShowRejectModal]       = useState(false);
  const [rejectReason, setRejectReason]             = useState('');

  // Inizializza lo stato dall'output esistente
  useEffect(() => {
    if (!execution) return;
    const out = execution.output || execution.outputData || {};

    const initial = {};
    scriptLibrary.forEach(s => { initial[s.script_id] = 'pending'; });
    (out.approved_scripts || []).forEach(s => { initial[s.script_id || s] = 'approved'; });
    (out.rejected_scripts || []).forEach(s => { initial[s.script_id || s] = 'rejected'; });
    setReviewMap(initial);

    // S* identifiability
    const initSStar = {};
    (out.approved_scripts || []).forEach(s => {
      if (s.s_star_identifiability) initSStar[s.script_id] = s.s_star_identifiability;
    });
    setSStarMap(initSStar);

    // Rejection reasons
    const initRejReasons = {};
    (out.rejected_scripts || []).forEach(s => {
      if (s.rejection_reason) initRejReasons[s.script_id] = s.rejection_reason;
    });
    setRejectionReasonMap(initRejReasons);

    // Selected script
    if (out.selected_approved_script?.script_id) {
      setSelectedScriptId(out.selected_approved_script.script_id);
      setSelectionRationale(out.selected_approved_script.selection_rationale || '');
    }

    setRevisionNotes(out.revision_notes || []);

    if ((out.approved_scripts || []).length > 0 || (out.rejected_scripts || []).length > 0) {
      setSavedOnce(true);
    }
  }, [execution?._id]);

  if (!execution) return <EmptyState />;

  const isReadOnly = execution.status === 'approved' || execution.status === 'rejected';

  const handleToggle = (scriptId, newState) => {
    if (isReadOnly) return;
    setReviewMap(prev => ({
      ...prev,
      [scriptId]: prev[scriptId] === newState ? 'pending' : newState
    }));
    // Auto-deselect if toggling away from approved
    if (newState !== 'approved' && selectedScriptId === scriptId) {
      setSelectedScriptId(null);
      setSelectionRationale('');
    }
  };

  const handleSelectScript = (scriptId) => {
    setSelectedScriptId(prev => prev === scriptId ? null : scriptId);
    if (selectedScriptId === scriptId) setSelectionRationale('');
  };

  const approvedScripts = scriptLibrary.filter(s => reviewMap[s.script_id] === 'approved');
  const rejectedScripts = scriptLibrary.filter(s => reviewMap[s.script_id] === 'rejected');
  const pendingScripts  = scriptLibrary.filter(s => !reviewMap[s.script_id] || reviewMap[s.script_id] === 'pending');

  const showSelectButton = approvedScripts.length > 1;

  const addNote = () => {
    if (newNote.trim()) {
      setRevisionNotes(prev => [...prev, newNote.trim()]);
      setNewNote('');
    }
  };

  const removeNote = (i) => setRevisionNotes(prev => prev.filter((_, idx) => idx !== i));

  const buildSavePayload = () => {
    const enrichedApproved = approvedScripts.map(s => ({
      ...s,
      approval_status: 'approved',
      s_star_identifiability: sStarMap[s.script_id] || { ...EMPTY_S_STAR },
      revision_notes: [],
      inferential_revision_notes: []
    }));

    const enrichedRejected = rejectedScripts.map(s => ({
      ...s,
      rejection_reason: rejectionReasonMap[s.script_id] || '',
      revision_notes: [],
      inferential_revision_notes: []
    }));

    let selectedScript = {};
    if (approvedScripts.length === 1) {
      selectedScript = {
        ...enrichedApproved[0],
        selection_rationale: 'Single approved script — auto-selected'
      };
    } else if (selectedScriptId) {
      const found = enrichedApproved.find(s => s.script_id === selectedScriptId);
      if (found) selectedScript = { ...found, selection_rationale: selectionRationale };
    }

    return {
      approved_scripts: enrichedApproved,
      selected_approved_script: selectedScript,
      rejected_scripts: enrichedRejected,
      revision_notes: revisionNotes,
      inferential_revision_notes: []
    };
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await contractsApi.submitReview(currentProjectId, 12, execution._id, buildSavePayload());
      setSavedOnce(true);
    } catch (err) {
      setSaveError(err.message || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleFinalApprove = async () => {
    await handleSave();
    onApprove();
  };

  const handleFinalReject = () => {
    if (rejectReason.trim()) {
      onReject(rejectReason);
      setShowRejectModal(false);
      setRejectReason('');
    }
  };

  // Validate: when multiple approved, one must be explicitly selected
  const canApprove = approvedScripts.length > 0 &&
    (approvedScripts.length === 1 || !!selectedScriptId);

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="max-w-4xl mx-auto space-y-6">

        {/* Header */}
        <div>
          <div className="flex items-center gap-3 mb-1">
            <ClipboardList className="w-6 h-6 text-fuchsia-600" />
            <h2 className="text-xl font-bold text-gray-800">Script Review</h2>
            <span className="px-2 py-0.5 text-xs bg-fuchsia-100 text-fuchsia-700 rounded-full font-medium">C12</span>
            <span className="px-2 py-0.5 text-xs bg-amber-100 text-amber-700 rounded-full font-medium">👤 Human Review</span>
          </div>
          <p className="text-sm text-gray-500">
            Approva o rifiuta ogni script. Valuta la S* Identifiability degli approvati. Poi approva la revisione complessiva.
          </p>
        </div>

        {/* Status banner */}
        <StatusBanner execution={execution} />

        {/* Contatori */}
        {scriptLibrary.length > 0 && (
          <div className="flex gap-3 flex-wrap">
            <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 text-center">
              <p className="text-xs text-gray-400">Totale</p>
              <p className="text-lg font-bold text-gray-700">{scriptLibrary.length}</p>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-2 text-center">
              <p className="text-xs text-green-500">Approvati</p>
              <p className="text-lg font-bold text-green-700">{approvedScripts.length}</p>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-2 text-center">
              <p className="text-xs text-red-500">Rifiutati</p>
              <p className="text-lg font-bold text-red-700">{rejectedScripts.length}</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 text-center">
              <p className="text-xs text-amber-500">Da rivedere</p>
              <p className="text-lg font-bold text-amber-700">{pendingScripts.length}</p>
            </div>
          </div>
        )}

        {/* Selection hint when multiple approved */}
        {!isReadOnly && approvedScripts.length > 1 && (
          <div className="bg-fuchsia-50 border border-fuchsia-200 rounded-lg p-3 flex items-start gap-2">
            <Star className="w-4 h-4 text-fuchsia-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-fuchsia-800">
              Hai approvato {approvedScripts.length} script. Seleziona quello canonical (★) che verrà propagato ai moduli downstream.
              {!selectedScriptId && <span className="font-medium"> Nessuno selezionato — richiesto prima di approvare.</span>}
            </p>
          </div>
        )}

        {/* Script list */}
        {scriptLibrary.length > 0 ? (
          <div className="space-y-3">
            {scriptLibrary.map((script, i) => (
              <ScriptReviewCard
                key={script.script_id || i}
                script={script}
                index={i}
                reviewState={reviewMap[script.script_id] || 'pending'}
                onToggle={handleToggle}
                sStarData={sStarMap[script.script_id] || {}}
                onSStarChange={data => setSStarMap(prev => ({ ...prev, [script.script_id]: data }))}
                rejectionReason={rejectionReasonMap[script.script_id] || ''}
                onRejectionReasonChange={val => setRejectionReasonMap(prev => ({ ...prev, [script.script_id]: val }))}
                isSelected={selectedScriptId === script.script_id}
                onSelect={handleSelectScript}
                showSelectButton={showSelectButton}
                selectionRationale={selectedScriptId === script.script_id ? selectionRationale : ''}
                onSelectionRationaleChange={setSelectionRationale}
                isReadOnly={isReadOnly}
              />
            ))}
          </div>
        ) : (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-700 text-sm">
            Nessuno script trovato. Verifica che il Contract 11 sia stato approvato correttamente.
          </div>
        )}

        {/* Revision Notes */}
        {!isReadOnly && (
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Note di revisione globali</h3>
            {revisionNotes.length > 0 && (
              <ul className="space-y-2 mb-3">
                {revisionNotes.map((note, i) => (
                  <li key={i} className="flex items-start gap-2 bg-gray-50 rounded-lg px-3 py-2">
                    <p className="flex-1 text-sm text-gray-700">{note}</p>
                    <button onClick={() => removeNote(i)} className="flex-shrink-0 text-gray-400 hover:text-red-500 transition-colors">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex gap-2">
              <input
                type="text"
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addNote()}
                placeholder="Aggiungi una nota di revisione globale..."
                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-fuchsia-500 focus:border-transparent"
              />
              <button onClick={addNote} disabled={!newNote.trim()}
                className="px-3 py-2 bg-fuchsia-600 text-white rounded-lg hover:bg-fuchsia-700 disabled:opacity-50">
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Read-only revision notes */}
        {isReadOnly && revisionNotes.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Note di revisione</h3>
            <ul className="space-y-1">
              {revisionNotes.map((note, i) => (
                <li key={i} className="text-sm text-gray-700 pl-3 border-l-2 border-fuchsia-200">{note}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Save error */}
        {saveError && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {saveError}
          </div>
        )}

        {/* Action buttons */}
        {!isReadOnly && (
          <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
            <h3 className="font-medium text-gray-800">Azioni</h3>

            <button
              onClick={handleSave}
              disabled={saving || loading}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 border-2 border-fuchsia-300 text-fuchsia-700 rounded-lg hover:bg-fuchsia-50 transition-colors disabled:opacity-50">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saving ? 'Salvataggio...' : savedOnce ? 'Salva modifiche' : 'Salva bozza'}
            </button>

            <button
              onClick={handleFinalApprove}
              disabled={saving || loading || !canApprove}
              title={!canApprove && approvedScripts.length > 1 ? 'Seleziona uno script canonical (★) prima di approvare' : ''}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Approva revisione ({approvedScripts.length} script approvati)
            </button>

            <button
              onClick={() => setShowRejectModal(true)}
              disabled={saving || loading}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50">
              <XCircle className="w-4 h-4" />
              Rigetta revisione
            </button>
          </div>
        )}

      </div>

      {/* Reject modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Motivo del Rigetto</h3>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Spiega perché rigetti questa revisione..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              rows={4}
            />
            <div className="flex gap-3 mt-4">
              <button onClick={() => setShowRejectModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                Annulla
              </button>
              <button onClick={handleFinalReject} disabled={!rejectReason.trim()}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">
                Conferma Rigetto
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Contract12View;
